
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from 'styled-components';
import { Box, Tabs, Tab, Typography, Container, Button, Grid, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import logo from "../assets/logo-white.png";
import heroImage from "../assets/heroimage.png";
import clinicalStudiesIcon from '../assets/lanpage/ClinicalStudies.png';
import clinicalStudiesPublicDomainIcon from '../assets/lanpage/Clinical Studies (Public Domain).png';
import stabilityStudiesIcon from '../assets/lanpage/Stability Studies.png';
import ipCreationIcon from '../assets/lanpage/IP Creation.png';
import dossierIcon from '../assets/lanpage/Dossier.png';
import dossierGrade2Icon from '../assets/lanpage/Dossiergrade2.png';
import toxicologyDataIcon from '../assets/lanpage/Toxicology Data.png';
import toxicologyDataGrade2Icon from '../assets/lanpage/Toxicology DataGrade2.png';
import toxicologyDataGrade3Icon from '../assets/lanpage/Toxicology DataGrade3.png';
import rationaleIcon from '../assets/lanpage/Rationale.png';
import rationaleGrade2Icon from '../assets/lanpage/RationaleGrade2.png';
import rationaleGrade3Icon from '../assets/lanpage/RationaleGrade3.png';
import synergyIcon from '../assets/lanpage/Synergy.png';
import synergyGrade2Icon from '../assets/lanpage/SynergyGrade2.png';
import synergyGrade3Icon from '../assets/lanpage/SynergyGrade3.png';
import mechanismOfActionIcon from '../assets/lanpage/Mechanism of Action.png';
import mechanismOfActionGrade2Icon from '../assets/lanpage/Mechanism of ActionGrade2.png';
import mechanismOfActionGrade3Icon from '../assets/lanpage/Mechanism of ActionGrade3.png';
import supplyChainIcon from '../assets/lanpage/Supply Chain.png';
import supplyChainGrade2Icon from '../assets/lanpage/Supply Chaingrade2.png';
import healthClaimsIcon from '../assets/lanpage/Health Claims.png';
import healthClaimsGrade2Icon from '../assets/lanpage/Health ClaimsGrade2.png';
import novelCombinationIcon from '../assets/lanpage/Novel Combination of Moleculesgrade3.png';
import medicalDossierIcon from '../assets/lanpage/Medical DossierGrade3.png';

// Keyframe animations from LoginStyles.js
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const ripple = keyframes`
  to {
    transform: scale(4);
    opacity: 0;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Global styles and CSS variables from LoginStyles.js
const globalStyles = `
  *, *:before, *:after {
    box-sizing: border-box;
  }

  :root {
    --primary-gradient: linear-gradient(90deg, #66202D 0%, #F89622 100%);
    --text-color: #F5F5F5;
    --active-glow: #FFE4CD;
    --neumorphic-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
`;

// Styled components for the navigation menu, adapted from LoginStyles.js
const NavContainer = styled.div`
  ${globalStyles}
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  box-shadow: none;
  padding: 0 117px;
  height: 360px;
  z-index: 1;

  @media (max-width: 768px) {
    background-attachment: scroll;
    padding: 1rem 1.5rem;
    height: 280px; /* Reduced height for mobile */
  }

  @media (max-width: 480px) {
    padding: 1rem;
    height: 240px; /* Further reduced for smaller screens */
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  position: relative;
  z-index: 10;
`;

const Logo = styled.img`
  width: 118px;
  height: 67px;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100px;
    height: 57px;
  }

  @media (max-width: 480px) {
    width: 80px;
    height: 46px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-family: 'Lato', sans-serif;

  a {
    position: relative;
    padding-bottom: 4px;
    color: white;
    text-decoration: none;
    font-weight: 700;
    font-size: 16px;
    text-transform: uppercase;
    transition: color 0.3s ease, transform 0.3s ease;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0%;
      height: 2px;
      background-color: #F89622;
      transition: width 0.3s ease;
    }

    &:hover {
      color: #FFE4CD;
      transform: translateY(-2px);
    }

    &:hover::after {
      width: 100%;
    }

    &.active::after {
      width: 100%;
    }
  }

  a.register {
    padding: 0.5rem 1.5rem;
    border: 1px solid #F89622;
    border-radius: 246px;
    background-color: transparent;
    color: white;
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;

    &::after {
      display: none;
    }

    &:hover {
      background-color: white;
      color: #F89622;
      transform: translateY(-2px);
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Hamburger = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 20px;
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
  }

  span {
    width: 100%;
    height: 3px;
    background-color: white;
    transition: all 0.3s ease;
  }

  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  &.open span:nth-child(2) {
    opacity: 0;
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -7px);
  }
`;

const MobileMenuOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 999;

  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'block' : 'none')};
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 70vh;
  background: var(--primary-gradient);
  backdrop-filter: blur(15px);
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem 1rem;
  border-bottom-left-radius: 32px;
  border-bottom-right-radius: 32px;
  box-shadow: var(--neumorphic-shadow);
  z-index: 1000;
  animation: ${slideDown} 0.4s ease-out forwards;
  clip-path: polygon(0 10%, 50% 0, 100% 10%, 100% 100%, 0 100%);

  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    height: 60vh;
  }
`;

const GrabberHandle = styled.div`
  width: 40px;
  height: 5px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  margin-top: 1rem;
  cursor: pointer;
`;

const MobileMenuLogo = styled.img`
  width: 90px;
  height: 50px;
  margin-bottom: 1rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  animation: ${spin} 1s ease-in-out;

  @media (max-width: 480px) {
    width: 70px;
    height: 39px;
  }
`;

const Divider = styled.hr`
  width: 80%;
  border: none;
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 1rem 0;
`;

const MenuLink = styled.a`
  position: relative;
  color: var(--text-color);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 20px;
  text-transform: uppercase;
  margin: 1rem 0;
  padding: 0.75rem 1.5rem;
  width: 100%;
  text-align: center;
  transition: color 0.3s ease, transform 0.3s ease;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: var(--neumorphic-shadow);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  &.active {
    color: var(--active-glow);
    box-shadow: 0 0 10px var(--active-glow);
  }

  &:hover {
    color: var(--active-glow);
    transform: scale(1.02);
  }

  &:focus {
    outline: 2px solid var(--active-glow);
    outline-offset: 2px;
  }

  .ripple {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 228, 205, 0.4);
    transform: scale(0);
    animation: ${ripple} 0.6s linear;
    pointer-events: none;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    padding: 0.5rem 1rem;
  }
`;

const Main = styled.main`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 10;
`;

// Main LandingPage component
const LandingPage = ({ setCurrentView = () => {}, navigate: propNavigate = () => {}, handleLogout = () => {} }) => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFAQToggle = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  // Handle ripple effect and vibration on link tap
  const handleLinkClick = (e, path) => {
    const link = e.currentTarget;
    const ripple = document.createElement("span");
    ripple.classList.add("ripple");
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    link.appendChild(ripple);

    // Vibration feedback (if supported)
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }

    setTimeout(() => {
      closeMenu();
      navigate(path);
    }, 200);
  };

  // Determine active link based on current path
  const getLinkClass = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const tabStyles = {
    0: { backgroundColor: '#FFF7D0', borderColor: '#DBB78D', iconColor: '#B99161', iconFill: '#FFFFFF' },
    1: { backgroundColor: '#E5F6F6', borderColor: '#C6E0E0', iconColor: '#ECF8F8', iconFill: '#5D8383' },
    2: { backgroundColor: '#E0EFFF', borderColor: '#C6E0E0', iconColor: '#EAF4FF', iconFill: '#6D8DB0' },
  };

  const currentTabStyle = tabStyles[tabValue];

  const faqs = [
    // Grade 1 FAQs (not provided in the PDF, so using a placeholder based on Grade 1 description)
    [
      {
        question: "Can I reserve a formula before purchasing? ",
        answer: "No, formulas can only be secured upon full purchase. Once purchased, the formula is exclusively locked for you. "
      },
      {
        question: "Is installment-based payment available?",
        answer: "Yes, flexible payment options, including installments, may be available. Please contact our team for tailored solutions. "
      },
      {
        question: "Will I receive support after purchasing a Grade One formula?",
        answer: "Absolutely. Post-purchase, you’ll receive expert guidance and ongoing support to help you navigate commercialization and implementation."
      },
      {
        question: "What kind of support can I expect for commercialization?",
        answer: "You’ll gain access to comprehensive support including tech transfer assistance, regulatory guidance, marketing insights, and supply chain facilitation to ensure a smooth market launch."
      }
    ],
    // Grade 2 FAQs (from PDF Page 2)
    [
      {
        question: "How do I choose the right formula for my business?",
        answer: "You decide the health category and market direction based on your own insights. Once you've chosen, we provide a scientifically robust, innovative formula tailored for commercialization."
      },
      {
        question: "What makes your formulas different from others in the market?",
        answer: "Our formulas are built on strong scientific rationale, unique ingredient synergy, mapped mechanisms of action, and complete technical documentation—not just trends."
      },
      {
        question: "Can I modify the formula after purchase?",
        answer: "Yes. Post-purchase, our team can support further optimization to align the formula with your brand’s specific needs or regulatory targets."
      },
      {
        question: "Do I get exclusive rights once I purchase a formula?",
        answer: "Yes. Once purchased, the formula is immediately locked and removed from circulation—granting you full commercial exclusivity."
      }
    ],
    // Grade 3 FAQs (from PDF Page 4)
    [
      {
        question: "Is Grade Three a ready-to-launch formula?",
        answer: "No, Grade Three is a validated concept with complete scientific support, but it requires prototyping, method validation, and regulatory approval before launch."
      },
      {
        question: "What kind of documentation will I receive?",
        answer: "You'll receive a full technical dossier, including mechanism of action, ingredient synergy, toxicology references, and scientific rationale."
      },
      {
        question: "Can I modify the concept during development?",
        answer: "Yes, the concept serves as a foundation—you can adapt or optimize it during formulation and prototyping to suit your market needs."
      },
      {
        question: "Will I get exclusive rights to the concept?",
        answer: "Yes, once purchased, the concept is locked and no longer available to others—giving you full ownership for commercialization."
      }
    ]
  ];

  const gradeIcons = [
    [
      clinicalStudiesIcon,
      stabilityStudiesIcon,
      ipCreationIcon,
      dossierIcon,
      toxicologyDataIcon,
      rationaleIcon,
      synergyIcon,
      mechanismOfActionIcon,
      supplyChainIcon,
      healthClaimsIcon,
    ],
    [
      clinicalStudiesPublicDomainIcon,
      dossierGrade2Icon,
      toxicologyDataGrade2Icon,
      rationaleGrade2Icon,
      synergyGrade2Icon,
      mechanismOfActionGrade2Icon,
      supplyChainGrade2Icon,
      healthClaimsGrade2Icon,
    ],
    [
      novelCombinationIcon,
      medicalDossierIcon,
      toxicologyDataGrade3Icon,
      rationaleGrade3Icon,
      synergyGrade3Icon,
      mechanismOfActionGrade3Icon,
    ],
  ];

  const gradeLabels = [
    ['Clinical Studies', 'Stability Studies', 'IP Creation', 'Dossier', 'Toxicology Data', 'Rationale', 'Synergy', 'Mechanism of Action', 'Supply Chain', 'Health Claims'],
    ['Clinical Studies (Public Domain)', 'Dossier', 'Toxicology Data', 'Rationale', 'Synergy', 'Mechanism of Action', 'Supply Chain', 'Health Claims'],
    ['Novel Combination of Molecules', 'Medical Dossier', 'Toxicology Data', 'Rationale', 'Synergy', 'Mechanism of Action'],
  ];

  const gradeDescriptions = [
    "Grade One is a clinically tested, custom supplement formula backed by comprehensive clinical data, toxicology reports, stability studies, validated methods, and detailed regulatory-approved claims. Each formula comes with full dossiers, GC analysis, mechanism of action, and documented ingredient synergy. With a secured supply chain and ready Bill of Materials, it’s innovation made simple. Just browse, buy, and sell—once you buy, the formula is locked exclusively for you.",
    "Grade Two is a scientifically validated supplement formula built on robust third-party clinical data, seamlessly integrated with strong chemistry rationale. Each formulation is mapped to biological receptors and supported by a clear mechanism of action. It includes comprehensive dossiers, ingredient synergy insights, GC analysis, and a ready Bill of Materials. While it doesn't include custom clinical trials, Grade Two offers a powerful, science-backed foundation for rapid commercialization. Browse, buy, and sell—with exclusive access granted upon purchase.",
    "Grade Three offers unique, science-driven supplement concepts designed for businesses "
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FFFFFF', position: 'relative', width: '100%', margin: '0 auto' }}>
      {/* Navigation Section */}
      <NavContainer bgImage={heroImage}>
        <Header>
          <Logo src={logo} alt="Nutrify Today Logo" />
          <Nav>
            <a href="/" className={getLinkClass("/")}>HOME</a>
            <a href="/about" className={getLinkClass("/about")}>ABOUT</a>
            <a href="/contact" className={getLinkClass("/contact")}>CONTACT</a>
            <a href="/login" className={getLinkClass("/login")}>LOGIN</a>
            <a href="/signup" className={`register ${getLinkClass("/signup")}`}>REGISTER</a>
          </Nav>
          <Hamburger
            className={menuOpen ? 'open' : ''}
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
          >
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
        </Header>
        <Box sx={{
          height: { xs: 'calc(240px - 64px)', sm: 'calc(280px - 64px)', md: 'calc(360px - 64px)' }, // Responsive height
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          color: '#FFFFFF',
          textAlign: 'left',
          paddingLeft: { xs: 2, md: '24px' }, // Reduced padding on mobile
        }}>
          <Typography sx={{
            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
            fontSize: { xs: '32px', sm: '40px', md: '48px' }, // Responsive font size
            color: '#FFFFFF',
            lineHeight: '150%', // Adjusted for better readability on mobile
          }}>
            <span style={{ fontWeight: 700 }}>Welcome to </span>
            <span style={{ fontWeight: 100 }}>Deal Sphere</span>
          </Typography>
          <Typography sx={{
            fontFamily: "'LT Renovate', sans-serif",
            fontWeight: 100,
            fontSize: { xs: '16px', sm: '18px', md: '20px' }, // Responsive font size
            color: '#FFFFFF',
            lineHeight: '130%',
          }}>
            Your destination for premium health and nutrition products
          </Typography>
          <Button
            onClick={() => navigate('/signup')}
            sx={{
              mt: 2,
              backgroundColor: '#F89622',
              color: '#FFFFFF',
              fontFamily: "'Lato', sans-serif",
              fontWeight: 700,
              fontSize: { xs: '12px', md: '14px' }, // Responsive font size
              textTransform: 'uppercase',
              borderRadius: '50px',
              width: { xs: '120px', md: '158px' }, // Responsive width
              height: { xs: '40px', md: '53px' }, // Responsive height
              '&:hover': { backgroundColor: '#e0851f' },
            }}
          >
            Explore Now
          </Button>
        </Box>
      </NavContainer>

      {/* Mobile Menu */}
      <Main>
        <MobileMenuOverlay isOpen={menuOpen} onClick={closeMenu} />
        <MobileMenu isOpen={menuOpen} ref={menuRef}>
          <MobileMenuLogo src={logo} alt="Nutrify Today Logo" />
          <Divider />
          <MenuLink
            href="/"
            className={getLinkClass("/")}
            onClick={(e) => handleLinkClick(e, "/")}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/")}
          >
            HOME
          </MenuLink>
          <MenuLink
            href="/about"
            className={getLinkClass("/about")}
            onClick={(e) => handleLinkClick(e, "/about")}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/about")}
          >
            ABOUT
          </MenuLink>
          <MenuLink
            href="/contact"
            className={getLinkClass("/contact")}
            onClick={(e) => handleLinkClick(e, "/contact")}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/contact")}
          >
            CONTACT
          </MenuLink>
          <MenuLink
            href="/login"
            className={getLinkClass("/login")}
            onClick={(e) => handleLinkClick(e, "/login")}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/login")}
          >
            LOGIN
          </MenuLink>
          <MenuLink
            href="/signup"
            className={`register ${getLinkClass("/signup")}`}
            onClick={(e) => handleLinkClick(e, "/signup")}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && handleLinkClick(e, "/signup")}
          >
            REGISTER
          </MenuLink>
          <GrabberHandle
            onClick={closeMenu}
            aria-label="Close menu"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && closeMenu()}
          />
        </MobileMenu>
      </Main>

      {/* Tab Content */}
      <Container sx={{ mt: { xs: 3, md: '40px' }, pb: 5, maxWidth: '1450px !important', width: '100%' }}>
        <Typography sx={{ 
          fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
          fontWeight: 400, 
          fontSize: { xs: '28px', sm: '36px', md: '48px' }, // Responsive font size
          color: '#000000', 
          textAlign: 'left', 
          mb: 3, 
          ml: { xs: 2, md: 3 }, // Reduced margin on mobile
        }}>
          Label Claims by Grade
        </Typography>
        <Box sx={{ backgroundColor: currentTabStyle.backgroundColor, border: `1px solid ${currentTabStyle.borderColor}`, borderRadius: 5, p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              mb: 0,
              pl: { xs: 2, md: 3 }, // Reduced padding on mobile
              '& .MuiTabs-flexContainer': {
                gap: { xs: '20px', md: '40px' }, // Reduced gap on mobile
                padding: { xs: '20px', md: '30px' },
                paddingBottom: '0px',
              },
              '& .MuiTab-root': {
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '18px', sm: '22px', md: '26px' }, // Responsive font size
                color: '#000000',
                textTransform: 'none',
                lineHeight: '120%',
                minWidth: 'auto',
                padding: { xs: '8px 0', md: '10px 0' },
                transition: 'color 0.3s ease',
                '&:hover': { color: '#F89622' },
              },
              '& .Mui-selected': { color: '#66202D' },
              '& .MuiTabs-indicator': {
                backgroundColor: '#F89622',
                height: { xs: '6px', md: '9px' }, // Smaller indicator on mobile
                borderRadius: '0px',
                transition: 'all 0.3s ease',
                bottom: 0,
              },
            }}
          >
            {['Grade - 1 (Ready to Launch)', 'Grade - 2 (Without Clinical Studies)', 'Grade - 3 (Novel Conceptualisation)'].map((label, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography sx={{
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontWeight: 700,
                      fontSize: { xs: '18px', sm: '22px', md: '26px' }, // Responsive font size
                      color: tabValue === index ? '#66202D' : '#000000',
                      lineHeight: '120%',
                    }}>
                      {label.split(' (')[0]}
                    </Typography>
                    <Typography sx={{
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontWeight: 400,
                      fontSize: { xs: '12px', sm: '14px', md: '16px' }, // Responsive font size
                      color: tabValue === index ? '#66202D' : '#000000',
                      lineHeight: '120%',
                      marginBottom: { xs: '6px', md: '10px' },
                    }}>
                      {`(${label.split(' (')[1]}`}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Box sx={{ width: '100%', height: 0, border: `1px solid ${currentTabStyle.borderColor}`, mb: 3 }} />

          {/* Tab Panels */}
          <Box>
            <Typography sx={{ 
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
              fontWeight: 400, 
              fontSize: { xs: '14px', md: '18px' }, // Responsive font size
              color: '#1E1E1E', 
              lineHeight: '160%', 
              mb: 3, // Reduced margin on mobile
              ml: { xs: 2, md: 3 }, // Reduced margin on mobile
              px: { xs: 1, md: 0 }, // Added padding for wrapping
            }}>
              {gradeDescriptions[tabValue]}
            </Typography>
            <Box sx={{ backgroundColor: currentTabStyle.backgroundColor, px: { xs: 2, md: 3 }, pb: 3 }}>
              <Grid container spacing={2}>
                {gradeIcons[tabValue].map((icon, index) => (
                  <Grid item xs={6} sm={4} md={2.4} key={index}> {/* 2 items per row on mobile, 3 on tablet, 5 on desktop */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: `1px solid ${currentTabStyle.borderColor}`,
                      borderRadius: 5,
                      backgroundColor: '#FFFFFF',
                      height: { xs: '60px', md: '80px' }, // Reduced height on mobile
                      padding: { xs: '10px', md: '20px' }, // Reduced padding on mobile
                    }}>
                      <img
                        src={icon}
                        alt={`${gradeLabels[tabValue][index]} Icon`}
                        style={{ 
                          width: { xs: '40px', md: '63px' }, // Responsive icon size
                          height: { xs: '40px', md: '63px' }, 
                          borderRadius: '50%' 
                        }}
                      />
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                        <Typography sx={{ 
                          fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
                          fontWeight: 400, 
                          fontSize: { xs: '12px', md: '18px' }, // Responsive font size
                          color: '#1E1E1E', 
                          lineHeight: '120%' 
                        }}>
                          {gradeLabels[tabValue][index]}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* FAQs Section */}
          <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
            <Typography sx={{ 
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
              fontWeight: 500, 
              fontSize: { xs: '16px', md: '21px' }, // Responsive font size
              color: '#000000', 
              mb: 2 
            }}>
              Frequently Asked Questions (FAQs)
            </Typography>
            {faqs[tabValue].map((faq, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Box
                  onClick={() => handleFAQToggle(index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <Typography sx={{ 
                    fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
                    fontWeight: 400, 
                    fontSize: { xs: '14px', md: '18px' }, // Responsive font size
                    color: '#1E1E1E', 
                    lineHeight: '160%' 
                  }}>
                    {`${index + 1}. ${faq.question}`}
                  </Typography>
                  {expandedFAQ === index ? <ExpandLessIcon sx={{ color: '#1E1E1E', fontSize: { xs: '20px', md: '24px' } }} /> : <ExpandMoreIcon sx={{ color: '#1E1E1E', fontSize: { xs: '20px', md: '24px' } }} />}
                </Box>
                <Collapse in={expandedFAQ === index}>
                  <Typography sx={{ 
                    fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
                    fontWeight: 400, 
                    fontSize: { xs: '14px', md: '18px' }, // Responsive font size
                    color: '#1E1E1E', 
                    lineHeight: '160%', 
                    pl: { xs: 3, md: 4 }, 
                    pb: 1 
                  }}>
                    {faq.answer}
                  </Typography>
                </Collapse>
                <Box sx={{ width: '100%', height: 0, border: '1px solid #E9D2B8' }} />
              </Box>
            ))}
            <Button
              onClick={() => navigate('/signup')}
              sx={{
                mt: 2,
                backgroundColor: '#F89622',
                color: '#FFFFFF',
                fontFamily: "'Lato', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '12px', md: '14px' }, // Responsive font size
                textTransform: 'uppercase',
                borderRadius: '50px',
                width: { xs: '120px', md: '158px' }, // Responsive width
                height: { xs: '40px', md: '53px' }, // Responsive height
                '&:hover': { backgroundColor: '#e0851f' },
              }}
            >
              Explore
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ 
        display: { xs: 'block', md: 'flex' }, // Stack on mobile, flex on desktop
        justifyContent: 'space-between', 
        alignItems: 'center', 
        px: { xs: 3, md: 15 }, // Reduced padding on mobile
        py: 3, 
        backgroundColor: '#FFFFFF',
        textAlign: { xs: 'center', md: 'left' }, // Center text on mobile
      }}>
        <Typography sx={{ 
          fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
          fontWeight: 400, 
          fontSize: { xs: '14px', md: '18px' }, // Responsive font size
          color: '#1E1E1E', 
          lineHeight: '200%',
          mb: { xs: 2, md: 0 }, // Add margin below on mobile
        }}>
          © 2025 DealSphere. All rights reserved.
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: { xs: 'center', md: 'flex-end' }, // Center links on mobile
          gap: { xs: 2, md: 4 }, // Reduced gap on mobile
          flexWrap: 'wrap', // Allow wrapping if needed
        }}>
          {['Terms', 'Privacy', 'Contact'].map((text) => (
            <Typography 
              key={text} 
              sx={{ 
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", 
                fontWeight: 400, 
                fontSize: { xs: '14px', md: '18px' }, // Responsive font size
                color: '#1E1E1E', 
                lineHeight: '200%' 
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;
